<script setup lang="ts">
import type { NuxtError } from '#app'

const props = defineProps<{
  error: NuxtError
}>()
const route = useRoute()
const user = useSupabaseUser()
const supabase = useSupabaseClient()
const { clear: clearPrivateClientState } = usePrivateClientState()
const switchingAccount = ref(false)
const switchError = ref('')
const proposalAccessDenied = computed(
  () =>
    props.error.statusCode === 403 &&
    (route.path.startsWith('/dashboard/proposals') ||
      (props.error.data as any)?.proposalAccessDenied),
)
const displayError = computed(() =>
  proposalAccessDenied.value
    ? {
        statusCode: 403,
        statusMessage: 'This account doesn’t have proposal access.',
        message: user.value?.email
          ? `You’re signed in as ${user.value.email}. Sign in with your authorized WLearn proposal-manager account.`
          : 'Sign in with your authorized WLearn proposal-manager account to continue.',
      }
    : props.error,
)

async function switchAccount() {
  switchingAccount.value = true
  switchError.value = ''
  try {
    const { error } = await supabase.auth.signOut()
    if (error) throw error
    await clearPrivateClientState()
    await clearError({ redirect: '/login?redirect=%2Fdashboard%2Fproposals' })
  } catch {
    switchError.value = 'Unable to sign out. Please try again.'
  } finally {
    switchingAccount.value = false
  }
}

useHead({ htmlAttrs: { lang: 'en' } })
useSeoMeta({
  title: () =>
    proposalAccessDenied.value
      ? 'Proposal access required'
      : `Error ${props.error.statusCode}`,
  robots: 'noindex, nofollow',
})
</script>

<template>
  <UApp>
    <div class="min-h-screen bg-[#f4fbff]">
      <MobileAppHeader />
      <UMain class="px-5 pb-12 pt-24">
        <UContainer>
          <section
            class="flex min-h-[calc(100dvh-9rem)] flex-col items-center justify-center text-center"
          >
            <p class="font-semibold text-blue-600">
              {{ displayError.statusCode }}
            </p>
            <h1
              class="mt-3 max-w-4xl text-3xl font-bold text-slate-900 sm:text-5xl"
            >
              {{ displayError.statusMessage || 'Something went wrong.' }}
            </h1>
            <p
              v-if="
                displayError.message &&
                displayError.message !== displayError.statusMessage
              "
              class="mt-5 max-w-2xl break-words leading-7 text-slate-600"
            >
              {{ displayError.message }}
            </p>
            <div class="mt-8">
              <div class="flex flex-col items-center gap-4">
                <UButton
                  v-if="proposalAccessDenied"
                  size="lg"
                  class="rounded-full px-6"
                  :loading="switchingAccount"
                  @click="switchAccount"
                >
                  Sign in with another account
                </UButton>
                <p v-if="switchError" role="alert" class="text-sm text-red-700">
                  {{ switchError }}
                </p>
                <UButton
                  size="lg"
                  variant="outline"
                  class="rounded-full px-6"
                  @click="clearError({ redirect: '/' })"
                  >Back to home</UButton
                >
              </div>
            </div>
          </section>
        </UContainer>
      </UMain>
    </div>
  </UApp>
</template>
